"use client"

import { css } from "@emotion/css"

import type { ButtonAttributes, ButtonsAttributes } from "@/../types/GutenbergBlockAttributes"
import withErrorBoundary from "@/shared-module/common/utils/withErrorBoundary"
import { Link } from "@/shared-module/components"
import colorMapper from "@/styles/course-material/colorMapper"
import { fontSizeMapper } from "@/styles/course-material/fontSizeMapper"
import { sanitizeCourseMaterialHtml } from "@/utils/course-material/sanitizeCourseMaterialHtml"

import type { BlockRendererProps } from "../../.."
import { OpensInNewTabNotice, relForLinkTarget } from "../../../util/links"

// Layout looks like this:
// export interface Layout {
//   justifyContent: string
//   orientation: string
//   type: string
// }

interface ExtraAttributes {
  orientation: string
  justifyContent: string
}

const getContentJustification = (contentJustification: string) => {
  const JUSTIFY_CENTER = "justify-content: center;"
  const JUSTIFY_FLEX_END = "justify-content: flex-end;"
  const JUSTIFY_SPACE_BETWEEN = "justify-content: space-between;"
  const JUSTIFY_FLEX_START = "justify-content: flex-start;"
  if (contentJustification === "center") {
    return JUSTIFY_CENTER
  } else if (contentJustification === "right") {
    return JUSTIFY_FLEX_END
  } else if (contentJustification === "space-between") {
    return JUSTIFY_SPACE_BETWEEN
  }
  return JUSTIFY_FLEX_START
}

const getAlignItems = (verticalAlignment: string) => {
  const ALIGN_CENTER = "align-items: center;"
  const ALIGN_FLEX_END = "align-items: flex-end;"
  const ALIGN_STRETCH = "align-items: stretch;"
  const ALIGN_FLEX_START = "align-items: flex-start;"
  if (verticalAlignment === "center") {
    return ALIGN_CENTER
  } else if (verticalAlignment === "bottom") {
    return ALIGN_FLEX_END
  } else if (verticalAlignment === "stretch") {
    return ALIGN_STRETCH
  }
  return ALIGN_FLEX_START
}

// TODO: Fetch the variant by extending CMS button attribute object, rather than using className generated by Gutenberg
const getButtonTypeFromClassName = (className: string | undefined) => {
  const PRIMARY = "primary"
  const SECONDARY = "secondary"
  const TERTIARY = "tertiary"
  if (!className) {
    return PRIMARY
  }

  if (className.includes("is-style-material-primary-button")) {
    return PRIMARY
  } else if (className.includes("is-style-material-secondary-button")) {
    return SECONDARY
  } else if (className.includes("is-style-material-tertiary-button")) {
    return TERTIARY
  }
  return PRIMARY
}

const ButtonsBlock: React.FC<
  React.PropsWithChildren<BlockRendererProps<ButtonsAttributes & ExtraAttributes>>
> = ({ data }) => {
  // Fall back to top-level attributes for blocks saved before settings moved into `layout`.
  const layout = data.attributes?.layout as
    | { orientation?: string; justifyContent?: string; verticalAlignment?: string }
    | undefined
  const orientation =
    layout?.orientation ?? (data.attributes?.orientation as string | undefined | null)
  const contentJustification =
    layout?.justifyContent ?? (data.attributes?.justifyContent as string | undefined | null)
  const verticalAlignment = layout?.verticalAlignment as string | undefined | null

  const mappedButtons = data.innerBlocks.map((button) => {
    const {
      // align,
      backgroundColor,
      fontSize,
      gradient,
      linkTarget,
      placeholder,
      rel,
      // style,
      text,
      textColor,
      // title
      url,
      className,
    } = button.attributes as ButtonAttributes & { className?: string }

    return (
      <Link
        key={button.clientId}
        styledAsButton
        variant={getButtonTypeFromClassName(className)}
        size="medium"
        href={url ?? ""}
        isDisabled={!url}
        {...(linkTarget && { target: linkTarget })}
        rel={relForLinkTarget(rel, linkTarget)}
        className={css`
          ${backgroundColor && `background: ${colorMapper(backgroundColor)} !important;`}
          ${gradient && `background: ${colorMapper(gradient)} !important;`}
          ${
            textColor &&
            `color: ${colorMapper(textColor)} !important; border-color: ${colorMapper(
              textColor,
            )} !important;`
          }
          ${fontSize && `font-size: ${fontSizeMapper(fontSize)} !important;`}
          margin: 0.5rem 0rem;
          margin-right: 0.5rem;
        `}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: sanitizeCourseMaterialHtml(text ?? placeholder ?? "BUTTON"),
          }}
        />
        <OpensInNewTabNotice linkTarget={linkTarget} />
      </Link>
    )
  })
  return (
    <div
      className={css`
        display: flex;
        flex-wrap: wrap;
        ${orientation === "vertical" ? "flex-direction: column;" : "flex-direction: row;"}
        ${contentJustification && getContentJustification(contentJustification)}
        ${verticalAlignment ? getAlignItems(verticalAlignment) : "align-items: flex-start;"}
      `}
    >
      {mappedButtons}
    </div>
  )
}

export default withErrorBoundary(ButtonsBlock)
